import React from 'react'
import classNames from 'classnames'

import type { WebsitetextVariant } from './constants'
import { textVariantsData } from './constants'

type BaseCustomTextProps = {
  htmlTag?: keyof HTMLElementTagNameMap
  variant: WebsitetextVariant
} & React.HTMLAttributes<HTMLDivElement>

type BaseHTMLDivTextProps = React.HTMLAttributes<HTMLDivElement> &
  BaseCustomTextProps

type BaseLinkTextProps = {
  href: string
} & React.AnchorHTMLAttributes<HTMLAnchorElement> &
  BaseCustomTextProps

type BaseHTMLLabelProps = {
  htmlFor: string
} & React.AnchorHTMLAttributes<HTMLLabelElement> &
  BaseCustomTextProps

const isCustomProps = (props: unknown): props is BaseCustomTextProps => {
  return Boolean((props as BaseCustomTextProps).htmlTag)
}

const isLinkProps = (props: unknown): props is BaseLinkTextProps => {
  return Boolean((props as BaseLinkTextProps).href)
}

const isLabelProps = (props: unknown): props is BaseHTMLLabelProps => {
  return Boolean((props as BaseHTMLLabelProps).htmlFor)
}

export type WebsiteTextProps =
  | BaseCustomTextProps
  | BaseHTMLDivTextProps
  | BaseLinkTextProps
  | BaseHTMLLabelProps

export const WebsiteText = ({
  children,
  className,
  variant,
  style: baseStyle,
  ...props
}: WebsiteTextProps) => {
  const classes = classNames(
    textVariantsData[variant].class,
    `website-text-${variant}`,
    className
  )
  const style = { ...baseStyle, ...textVariantsData[variant].style }
  const defaultHtmlTag = textVariantsData[variant].defaultHtmlTag || 'div'

  if (isCustomProps(props)) {
    const { htmlTag, ...domProps } = props
    return React.createElement(
      htmlTag || defaultHtmlTag,
      { ...domProps, className: classes, style },
      children
    )
  }

  if (isLabelProps(props)) {
    return (
      <label className={classes} style={style} {...props}>
        {children}
      </label>
    )
  }

  if (isLinkProps(props)) {
    return (
      <a className={classes} style={style} {...props}>
        {children}
      </a>
    )
  }

  return React.createElement(
    defaultHtmlTag,
    { ...props, className: classes, style },
    children
  )
}
